import React, {
    ForwardedRef,
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useReducer,
} from 'react';
import classNames from 'classnames';
import { Icon, MessageIcon } from '@instinet-analytics/semantic-ui-components';
import './IndexMappingsEditorPanel.scss';
import indexMappingsSliceReducer, {
    actions,
    IndexMappingEditorRow,
    IndexMappingsEditorPanelState,
    initialIndexMappingsEditorPanelState,
} from './IndexMappingsEditorPanelState';
import {
    getIsValid,
    validateIndexMappings,
} from 'symbols/indexMappings/IndexMappingsValidation';
import { IndexMappingsEditorModalHelp } from '../IndexMappingsEditorModal/IndexMappingsEditorModalHelp';
import { useSymbolsService } from 'symbols/hooks/useSymbolsService';
import { useSelectAppSettings } from 'app/store/appSettings/selectors';
import { SymbolInfoAttributeDropdown } from 'symbols/symbolInfo/SymbolInfoAttributeDropdown';
import { SymbolInfoValueEditor } from 'symbols/symbolInfo/SymbolInfoValueEditor';
import { useFetchIndexMappingsUserPreference } from 'symbols/indexMappings/hooks';
import { IndexMapping } from '../store/IndexMappingsState';
import { TestSymbol } from 'symbols/indexMappings/TestSymbol';
import { useSelectIndexMappings } from 'symbols/indexMappings/store/hooks';
import { SymbolInfoColumns } from 'symbols/symbolInfo/columns';
import SymbolInput from 'symbols/components/SymbolInput';

interface IndexMappingEditorPanelProps {
    className?: string;
    onOkToSaveChanged: (okToSave: boolean) => void;
    showTestPanel?: boolean;
}

export interface IndexMappingEditorPanelRef {
    saveMappings: () => Promise<boolean>;
    reset: () => void;
}

// eslint-disable-next-line react/display-name
export const IndexMappingsEditorPanel = forwardRef(
    (
        props: IndexMappingEditorPanelProps,
        ref: ForwardedRef<IndexMappingEditorPanelRef>
    ) => {
        const { className, onOkToSaveChanged, showTestPanel } = props;

        const userPrefConvertedMappings = useSelectIndexMappings();
        useFetchIndexMappingsUserPreference();

        const symbolsService = useSymbolsService();
        const { symbology } = useSelectAppSettings();

        const initialState: IndexMappingsEditorPanelState = {
            ...initialIndexMappingsEditorPanelState,
            editingMappings: userPrefConvertedMappings,
        };

        const [state, dispatch] = useReducer(
            indexMappingsSliceReducer,
            initialState
        );

        const { editingMappings, validationResult, isDirty, saveAttempted } =
            state;

        useEffect(() => {
            const disableSave =
                saveAttempted && isDirty && !getIsValid(validationResult);
            const okToSave = !disableSave;
            onOkToSaveChanged(okToSave);
        }, [saveAttempted, onOkToSaveChanged, isDirty, validationResult]);

        useEffect(() => {
            // Set on first time retrieval...
            if (editingMappings === undefined && userPrefConvertedMappings) {
                dispatch(
                    actions.setEditingMappings({
                        mappings: userPrefConvertedMappings,
                        suppressSetDirty: true,
                    })
                );
            }
        }, [editingMappings, userPrefConvertedMappings]);

        useEffect(() => {
            const validationResults = validateIndexMappings(
                editingMappings,
                symbolsService,
                symbology
            );
            dispatch(actions.setValidationResult(validationResults));
        }, [editingMappings, symbology, symbolsService]);

        const saveMappings: () => Promise<boolean> = () => {
            dispatch(actions.saveAttempted());

            const valid = getIsValid(validationResult);

            if (!valid || !editingMappings) {
                return Promise.resolve(false);
            }

            if (!isDirty) {
                return Promise.resolve(true);
            }

            (async () => {
                try {
                    await symbolsService.saveIndexMappings(
                        symbology,
                        editingMappings as IndexMapping[]
                    );
                } catch {
                    return false;
                }
            })();
            return Promise.resolve(true);
        };
        const reset = () => {
            dispatch(actions.reset());
        };

        useImperativeHandle(ref, () => ({
            saveMappings,
            reset,
        }));

        const onUpdateMapping = useCallback(
            (ordinal: number, indexMapping: IndexMappingEditorRow) => {
                dispatch(actions.update({ ordinal, indexMapping }));
            },
            [dispatch]
        );

        const onIndexValidationStatusChanged = useCallback(
            (ordinal: number, value: string | undefined, isValid: boolean) => {
                const valid = !!value && isValid;
                dispatch(
                    actions.setMappingIndexValid({ ordinal, isValid: valid })
                );
            },
            [dispatch]
        );

        const onDeleteMapping = useCallback(
            (ordinal: number) => {
                dispatch(actions.remove({ ordinal }));
            },
            [dispatch]
        );

        const onClickAdd = useCallback(() => {
            dispatch(actions.add());
        }, [dispatch]);

        const onMove = useCallback(
            (ordinal: number, direction: 'up' | 'down') => {
                dispatch(actions.move({ ordinal, direction }));
            },
            [dispatch]
        );

        return (
            <div className={classNames('IndexMappingsEditorPanel', className)}>
                <IndexMappingsEditorModalHelp />
                <div className="simple-grid">
                    <div className="grid-row grid-row-header">
                        <div className="mapping-attribute">Attribute</div>
                        <div className="mapping-value">Value</div>
                        <div className="mapping-index">Index</div>
                        <div className="col-actions" />
                    </div>
                    {editingMappings &&
                        editingMappings.map((r, i) => (
                            <MappingRow
                                key={i.toString()}
                                saveAttempted={saveAttempted}
                                mapping={r}
                                first={i === 0}
                                last={i === editingMappings.length - 1}
                                onUpdateMapping={(nextMapping) =>
                                    onUpdateMapping(i, nextMapping)
                                }
                                onDeleteMapping={() => onDeleteMapping(i)}
                                onMove={(direction: 'up' | 'down') =>
                                    onMove(i, direction)
                                }
                                onIndexValidationStatusChanged={(
                                    value: string | undefined,
                                    isValid: boolean
                                ) =>
                                    onIndexValidationStatusChanged(
                                        i,
                                        value,
                                        isValid
                                    )
                                }
                            />
                        ))}
                    <div className="grid-row add-row">
                        <div
                            className="mapping-value link-text"
                            onClick={onClickAdd}
                        >
                            Add...
                        </div>
                    </div>
                </div>
                {showTestPanel && (
                    <TestSymbol
                        mappings={editingMappings ?? []}
                        configuredFallbackIndex={''}
                    />
                )}
            </div>
        );
    }
);

function MappingRow(props: {
    saveAttempted: boolean;
    first: boolean;
    last: boolean;
    mapping: IndexMappingEditorRow;
    onUpdateMapping: (nextMapping: IndexMappingEditorRow) => void;
    onDeleteMapping: () => void;
    onMove: (direction: 'up' | 'down') => void;
    onIndexValidationStatusChanged?: (
        value: string | undefined,
        isValid: boolean
    ) => void;
}) {
    const {
        saveAttempted,
        first,
        last,
        mapping,
        onUpdateMapping,
        onDeleteMapping,
        onMove,
        onIndexValidationStatusChanged,
    } = props;
    const { index, attribute, value } = mapping;

    const onUpdateAttribute = useCallback(
        (nextAttribute: SymbolInfoColumns) => {
            onUpdateMapping({
                ...mapping,
                attribute: nextAttribute,
            });
        },
        [onUpdateMapping, mapping]
    );

    const onUpdateValue = useCallback(
        (nextValue: string) => {
            onUpdateMapping({
                ...mapping,
                value: nextValue,
            });
        },
        [onUpdateMapping, mapping]
    );

    const onUpdateIndex = useCallback(
        (nextIndex: string) => {
            onUpdateMapping({ ...mapping, index: nextIndex });
        },
        [onUpdateMapping, mapping]
    );

    const onMoveUp = () => onMove('up');
    const onMoveDown = () => onMove('down');

    const symbolMissing = !index || index === '';
    const attributeMissing = !attribute;

    const cssClasses = classNames(
        'mapping-row grid-row',
        first && 'first-row',
        last && 'last-row',
        saveAttempted && 'show-validation-errors',
        saveAttempted && symbolMissing && 'symbol-missing',
        saveAttempted && attributeMissing && 'attribute-missing'
    );

    return (
        <div className={cssClasses}>
            <div className="grid-col mapping-attribute">
                <SymbolInfoAttributeDropdown
                    currentValue={attribute}
                    onChanged={onUpdateAttribute}
                />
                {saveAttempted && attributeMissing && (
                    <MessageIcon title={`Please select an attribute`} />
                )}
            </div>

            <div className="even-split-col">
                <div className="grid-col mapping-value">
                    <SymbolInfoValueEditor
                        value={value}
                        attribute={attribute}
                        onChanged={onUpdateValue}
                    />
                </div>
                <div className="grid-col mapping-index">
                    <SymbolInput
                        value={index}
                        onChange={onUpdateIndex}
                        onValidationStatusChanged={
                            onIndexValidationStatusChanged
                        }
                    />
                    {saveAttempted && symbolMissing && (
                        <MessageIcon title={`Please enter a symbol or index`} />
                    )}
                </div>
            </div>
            <div className="grid-col col-actions">
                <Icon
                    name="trash"
                    className="delete-button"
                    title={`Delete`}
                    onClick={onDeleteMapping}
                />

                <div className="move-icons-container">
                    <Icon
                        className="action"
                        name="caret up"
                        onClick={onMoveUp}
                    />
                    <Icon
                        className="action"
                        name="caret down"
                        onClick={onMoveDown}
                    />
                </div>
            </div>
        </div>
    );
}
